let zipCodesFileUrl: string | undefined;
let lastLoadedUrl: string | undefined;
let cachedZipSet: ReadonlySet<string> | null = null;
let loadingPromise: Promise<ReadonlySet<string>> | null = null;

function normalizeZip(value: unknown): string | null {
	const five = String(value ?? '').match(/\d{5}/)?.[0];
	if (!five) return null;
	return five;
}

async function loadZipCodes(): Promise<ReadonlySet<string>> {
	if (cachedZipSet) return cachedZipSet;
	if (loadingPromise) return loadingPromise;
	if (!zipCodesFileUrl) return new Set<string>();

	loadingPromise = (async () => {
		try {
			const response = await fetch(zipCodesFileUrl, { cache: 'no-cache' });
			const buffer = await response.arrayBuffer();
			const XLSX = await import(/* webpackChunkName: "xlsx" */ 'xlsx');
			const workbook = XLSX.read(buffer, { type: 'array' });
			const sheet = workbook.Sheets[workbook.SheetNames[0]];
			const rows: unknown[][] = XLSX.utils.sheet_to_json(sheet, { header: 1 });
			if (!rows?.length) return new Set<string>();

			// Find header row with 'ZIP' or third column in excel file
			let headerIndex = 0;
			let zipColIndex = 2; // (3rd column)
			for (let i = 0; i < Math.min(10, rows.length); i += 1) {
				const header = (rows[i] as unknown[])?.map?.((v: unknown) => String(v ?? '').trim());
				if (header?.some?.((v: string) => /^(zip(\s*code)?)$/i.test(v))) {
					headerIndex = i;
					zipColIndex = header.findIndex((v: string) => /^(zip(\s*code)?)$/i.test(v));
					break;
				}
			}

			const set = new Set<string>();
			for (let r = headerIndex + 1; r < rows.length; r += 1) {
				const raw = rows[r]?.[zipColIndex];
				const zip = normalizeZip(raw);
				if (zip) set.add(zip);
			}

			cachedZipSet = set;
			lastLoadedUrl = zipCodesFileUrl;
			return set;
		} catch {
			cachedZipSet = new Set<string>();
			return cachedZipSet;
		} finally {
			loadingPromise = null;
		}
	})();
	return loadingPromise;
}

export function initZipCodes(url?: string): void {
	zipCodesFileUrl = url || zipCodesFileUrl;
	if (!zipCodesFileUrl) return;
	if (lastLoadedUrl && lastLoadedUrl !== zipCodesFileUrl) {
		cachedZipSet = null;
	}
	loadZipCodes().catch(() => undefined);
}

export function isZipAllowed(zip: string | undefined): boolean {
	const five = normalizeZip(zip);
	if (!five) return false;
	if (cachedZipSet == null) {
		if (zipCodesFileUrl && !loadingPromise) loadZipCodes().catch(() => undefined);
		return true;
	}
	return cachedZipSet.has(five);
}

export function clearZipCache(): void {
	cachedZipSet = null;
	lastLoadedUrl = undefined;
}

